import { inject, Injectable } from '@angular/core';
import {
  DARK_MODE,
  LIGHT_MODE,
  MODE_CACHE_KEY,
} from '@core/constants/mode.constants';
import { TranslateService } from '@ngx-translate/core';
import {
  Confirmation,
  ConfirmationService,
  MessageService,
  ToastMessageOptions,
} from 'primeng/api';
import { BehaviorSubject, Observable } from 'rxjs';
import { CacheService } from './cache.service';

@Injectable({
  providedIn: 'root',
})
export class UIService {
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly cacheService = inject(CacheService);

  private isLoadingSubject$ = new BehaviorSubject(false);
  readonly isLoading$ = this.isLoadingSubject$.asObservable();

  setIsLoading(isLoading: boolean) {
    this.isLoadingSubject$.next(isLoading);
  }

  mode$ = new BehaviorSubject<string>(
    this.cacheService.getItem<string>(MODE_CACHE_KEY) || LIGHT_MODE,
  );

  initMode() {
    const mode =
      this.cacheService.getItem<string>(MODE_CACHE_KEY) || LIGHT_MODE;
    if (mode === DARK_MODE) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  toggleMode() {
    const mode =
      this.cacheService.getItem<string>(MODE_CACHE_KEY) || LIGHT_MODE;
    if (mode === DARK_MODE) {
      document.documentElement.classList.remove('dark');
    } else {
      document.documentElement.classList.add('dark');
    }

    const newMode = mode === DARK_MODE ? LIGHT_MODE : DARK_MODE;
    this.mode$.next(newMode);
    this.cacheService.setItem(MODE_CACHE_KEY, newMode);
  }

  showToast(options: ToastMessageOptions) {
    this.messageService.add(options);
  }

  showConfirmation(options: Confirmation) {
    this.confirmationService.confirm({
      ...options,
    });
  }

  confirmAction(
    title: string,
    message: string,
    confirmButtonColor = 'danger',
  ): Observable<boolean> {
    return new Observable((subscriber) => {
      this.confirmationService.confirm({
        header: title,
        message,
        accept: () => {
          subscriber.next(true);
          subscriber.complete();
        },
        reject: () => {
          subscriber.next(false);
          subscriber.complete();
        },
        acceptButtonProps: {
          label: this.translateService.instant('common.yes'),
          severity: confirmButtonColor,
        },
        rejectButtonProps: {
          label: this.translateService.instant('common.no'),
          severity: 'secondary',
        },
      });
    });
  }
}
